import React, { FC, ReactNode, useEffect, useState } from 'react'
import { usePopperTooltip, TriggerType } from 'react-popper-tooltip'
import Button, { ButtonProps } from '../Button/ButtonV2'
import { X } from 'react-bootstrap-icons'
import { isString } from '@/utils/checks'

export interface BottomSheetProps {
  header: string | ReactNode
  description: string
  buttonText?: string
  buttonLink?: string
  buttonIcon?: ReactNode
  buttonType?: ButtonProps['type']
  delay?: number
  trigger?: TriggerType | TriggerType[] | null
  distance?: number
  hidden?: boolean
  className?: string
  children: ReactNode
}

const BottomSheet: FC<BottomSheetProps> = ({
  header,
  description = '',
  buttonText = '',
  buttonLink = '',
  buttonIcon = '',
  buttonType = 'secondary',
  delay = 0,
  trigger = 'click',
  distance = 6,
  hidden = false,
  className = '',
  children
}) => {
  const [controlledVisible, setControlledVisible] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [isMounted, setIsMounted] = useState(false)

  const { setTooltipRef, setTriggerRef, visible } = usePopperTooltip({
    delayShow: delay,
    offset: [0, distance],
    trigger,
    closeOnTriggerHidden: true,
    visible: controlledVisible,
    onVisibleChange: setControlledVisible
  })

  useEffect(() => {
    if (visible) {
      setIsClosing(false)
      setIsMounted(true)
    } else {
      setIsClosing(true)
      const timer = setTimeout(() => setIsMounted(false), 300)
      return () => clearTimeout(timer)
    }
  }, [visible])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setControlledVisible(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  useEffect(() => {
    document.documentElement.style.overflow = visible ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [visible])

  if (hidden) {
    return <>{children}</>
  }

  return (
    <>
      <div
        className="block"
        ref={setTriggerRef}
      >
        {children}
      </div>

      {isMounted && (
        <div className="fixed left-0 top-0 z-40 h-screen w-screen bg-gray-1000/50">
          <div
            className={`pointer-events-auto fixed bottom-0 z-50 flex max-h-[70vh] w-full rounded-t-xl bg-white shadow-md ${
              isClosing ? 'animate-slide-down' : 'animate-slide-up'
            }`}
          >
            <div
              ref={setTooltipRef}
              className={`relative flex w-full flex-col gap-6 px-6 py-9 ${className}`}
            >
              <button
                className="absolute right-2 top-2 rounded-full p-2"
                onClick={() => setControlledVisible(false)}
              >
                <X
                  size="32"
                  color="#677D95"
                />
              </button>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col items-start gap-2">
                  {isString(header) ? (
                    <h3 className="text-lg font-semibold text-gray-1100">
                      {header}
                    </h3>
                  ) : (
                    header
                  )}
                  <p className="max-h-[45vh] overflow-auto text-lg text-gray-800">
                    {description}
                  </p>
                </div>
              </div>
              {buttonLink && buttonText && (
                <div>
                  <Button
                    stopPropagation={true}
                    as="a"
                    href={buttonLink}
                    type={buttonType}
                    className="w-full"
                  >
                    {buttonText}
                    {buttonIcon}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default BottomSheet
